import { Button } from "@common/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@common/components/ui/dropdown-menu"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { PaneNumber } from "@/components/layout/detail-panel"
import { StepStateIcon } from "@/routes/_layout/planning/-components/plan-status"
import { formatDays, formatSpan } from "@/routes/_layout/planning/-lib/days"
import { withStepState } from "@/routes/_layout/planning/-lib/edits"
import { displayOf } from "@/routes/_layout/planning/-lib/plan-search"
import {
  barOf,
  crewFor,
  type Plan,
  STEP_STATES,
  type Step,
  stepRef,
  stepState,
  stepStateLabel,
  taskRef,
  taskState,
  tasksOf,
} from "@/routes/_layout/planning/-lib/plans"
import { updatePlan, usePlan } from "@/routes/_layout/planning/-lib/store"
import {
  Chip,
  People,
} from "@/routes/_layout/planning_/$planId/-components/chip"
import {
  SheetIntro,
  SheetPanel,
  SheetRow,
  SheetSection,
  SheetTitle,
} from "@/routes/_layout/planning_/$planId/-components/sheet"
import ExpandMore from "~icons/material-symbols/expand-more"
import KeyboardArrowDown from "~icons/material-symbols/keyboard-arrow-down"
import KeyboardArrowUp from "~icons/material-symbols/keyboard-arrow-up"
import ViewTimelineOutline from "~icons/material-symbols/view-timeline-outline"

export const Route = createFileRoute(
  "/_layout/planning_/$planId/steps/$stepId/",
)({
  component: StepSheet,
})

function StepSheet(): React.ReactElement | null {
  const { planId, stepId } = Route.useParams()
  const plan = usePlan(planId)
  const navigate = useNavigate()
  const index = plan?.steps.findIndex((step) => step.id === stepId) ?? -1
  const step = plan?.steps[index]
  if (!plan || !step) return null

  const close = () =>
    navigate({
      to: "/planning/$planId",
      params: { planId },
      search: true,
    })
  const neighbour = (offset: number) =>
    plan.steps[(index + offset + plan.steps.length) % plan.steps.length]?.id ??
    stepId

  return (
    <SheetPanel
      title={step.name}
      onClose={close}
      heading={
        <>
          <PaneNumber>{stepRef(plan, step.id)}</PaneNumber>
          <StateMenu plan={plan} step={step} />
        </>
      }
      actions={
        <>
          <span className="px-1 text-small-regular text-muted-foreground">
            {index + 1} of {plan.steps.length}
          </span>
          <StepLink plan={plan} stepId={neighbour(-1)} label="Previous step">
            <KeyboardArrowUp />
          </StepLink>
          <StepLink plan={plan} stepId={neighbour(1)} label="Next step">
            <KeyboardArrowDown />
          </StepLink>
        </>
      }
    >
      <StepIntro plan={plan} step={step} />
      <StepSections plan={plan} step={step} />
    </SheetPanel>
  )
}

function StepLink({
  plan,
  stepId,
  label,
  children,
}: {
  plan: Plan
  stepId: string
  label: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <Button
      asChild
      variant="ghost"
      size="icon-sm"
      className="text-muted-foreground"
      aria-label={label}
      title={label}
    >
      <Link
        to="/planning/$planId/steps/$stepId"
        params={{ planId: plan.id, stepId }}
        search={true}
      >
        {children}
      </Link>
    </Button>
  )
}

function StateMenu({
  plan,
  step,
}: {
  plan: Plan
  step: Step
}): React.ReactElement {
  const state = stepState(plan, step.id)
  const pick = (value: string) => {
    const next = STEP_STATES.find((option) => option === value)
    if (next)
      updatePlan(plan.id, (current) => withStepState(current, step.id, next))
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 text-small-regular">
          <StepStateIcon state={state} />
          {stepStateLabel(state)}
          <ExpandMore className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        <DropdownMenuRadioGroup value={state} onValueChange={pick}>
          {STEP_STATES.map((option) => (
            <DropdownMenuRadioItem
              key={option}
              value={option}
              className="gap-2"
            >
              <StepStateIcon state={option} />
              {stepStateLabel(option)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function StepIntro({
  plan,
  step,
}: {
  plan: Plan
  step: Step
}): React.ReactElement {
  const span = barOf(plan, step.id)
  const crew = crewFor(step)
  const [lead = ""] = crew
  const focused = displayOf(Route.useSearch()).focus === step.id
  const crewLabel =
    crew.length === 1 ? lead : `${lead.split(" ")[0]} +${crew.length - 1}`

  return (
    <SheetIntro>
      <SheetTitle>{step.name}</SheetTitle>
      <div className="flex flex-wrap gap-1.5">
        <Chip className="h-7 text-small-regular">
          <span className="text-muted-foreground">Department</span>
          {step.shop}
        </Chip>
        <Chip className="h-7 text-small-regular">
          <People names={crew} />
          {crewLabel}
        </Chip>
        <Chip className="h-7 text-small-regular">
          {formatSpan(span.start, span.end)}
        </Chip>
        <Chip className="h-7 text-small-regular">{formatDays(step.days)}</Chip>
        {!focused && (
          <Button asChild variant="secondary" size="sm">
            <Link
              from="/planning/$planId/steps/$stepId/"
              to="/planning/$planId"
              params={{ planId: plan.id }}
              search={(previous) => ({
                ...previous,
                view: undefined,
                focus: step.id,
              })}
            >
              <ViewTimelineOutline className="text-muted-foreground" />
              View timeline
            </Link>
          </Button>
        )}
      </div>
    </SheetIntro>
  )
}

function StepSections({
  plan,
  step,
}: {
  plan: Plan
  step: Step
}): React.ReactElement {
  const dependsOn = step.deps.flatMap(
    (dep) => plan.steps.find((each) => each.id === dep) ?? [],
  )
  const blocks = plan.steps.filter((each) => each.deps.includes(step.id))
  const tasks = tasksOf(plan, step)
  const crew = crewFor(step)

  return (
    <>
      <SheetSection title="Depends on" empty="No dependencies">
        {dependsOn.map((other) => (
          <StepRow key={other.id} plan={plan} step={other} />
        ))}
      </SheetSection>
      <SheetSection title="Blocks" empty="Nothing waits on this step">
        {blocks.map((other) => (
          <StepRow key={other.id} plan={plan} step={other} />
        ))}
      </SheetSection>
      <SheetSection title="Tasks" empty="None">
        {tasks.map((task, index) => (
          <Link
            key={`${task.name}:${task.start}`}
            to="/planning/$planId/steps/$stepId/tasks/$taskIndex"
            params={{
              planId: plan.id,
              stepId: step.id,
              taskIndex: String(index),
            }}
            search={true}
            className="block hover:no-underline"
          >
            <SheetRow
              icon={<StepStateIcon state={taskState(plan, step, task)} />}
              reference={taskRef(step, index)}
              name={task.name}
              detail={formatSpan(task.start, task.end)}
              people={[crew[index % crew.length] ?? ""]}
            />
          </Link>
        ))}
      </SheetSection>
    </>
  )
}

function StepRow({
  plan,
  step,
}: {
  plan: Plan
  step: Step
}): React.ReactElement {
  const span = barOf(plan, step.id)
  return (
    <Link
      to="/planning/$planId/steps/$stepId"
      params={{ planId: plan.id, stepId: step.id }}
      search={true}
      className="block hover:no-underline"
    >
      <SheetRow
        icon={<StepStateIcon state={stepState(plan, step.id)} />}
        name={step.name}
        detail={formatSpan(span.start, span.end)}
        people={crewFor(step)}
      />
    </Link>
  )
}
